import { memo, useMemo } from 'react'
import { replace } from 'connected-react-router'

import { useMutation } from '@tanstack/react-query'

import Routes from '@tabeeb/routes'
import { useDispatch } from '@tabeeb/store'
import { useQuery } from '@tabeeb/shared/utils/hooks'
import { trainingMaterialTitleFormatter } from '@tabeeb/shared/utils/text'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'

import { ArticleContentType, ArticleReadScope, ArticlePublicationStatus } from '@tabeeb/modules/articles/enums'

import { useApi } from '@tabeeb/modules/shared/utils/API'

import TrainingMaterialEditorPage from '../TrainingMaterialEditorPage'

const TrainingMaterialCreatePage: React.FC = () => {
  const dispatch = useDispatch()

  const type = useQuery().get('type')

  const contentType = useMemo(() => {
    const parsed = parseInt(type ?? ArticleContentType.Html.toString(), 10)
    if (Object.values(ArticleContentType).includes(parsed)) {
      return parsed
    }

    return ArticleContentType.Html
  }, [type])

  const { articleServiceApi: api } = useApi()

  const createMutation = useMutation({
    mutationFn: api.trainingMaterials.createAsync,
    onSuccess: () => {
      dispatch(
        onAddSuccessNotification({
          message: trainingMaterialTitleFormatter.format('Training material successfully created'),
        })
      )
      dispatch(replace(Routes.trainingMaterials))
    },
    onError: () => {
      dispatch(
        onAddErrorNotification({
          message: trainingMaterialTitleFormatter.format('Failed to create training material'),
        })
      )
    },
  })

  const initialValues = useMemo(
    () => ({
      Name: trainingMaterialTitleFormatter.format('New training material'),
      Content: {
        Type: contentType,
        Content: '',
      },
      ReadScope: ArticleReadScope.Content,
      Status: ArticlePublicationStatus.Draft,
    }),
    [contentType]
  )

  return (
    <TrainingMaterialEditorPage
      initial={initialValues}
      title={trainingMaterialTitleFormatter.format('Create training material')}
      onSubmit={createMutation.mutate}
    />
  )
}

export default memo(TrainingMaterialCreatePage)
